import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Fonction utilitaire pour calculer la régression linéaire
const calculateLinearRegression = (data) => {
  const n = data.length;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;

  data.forEach((point, index) => {
    sumX += index;
    sumY += point;
    sumXY += index * point;
    sumXX += index * index;
  });

  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  return { slope, intercept };
};

const SalesForecast = ({ orders }) => {
  const forecastData = useMemo(() => {
    // Grouper les commandes par jour
    const dailyOrders = {};
    const dailyRevenue = {};
    
    orders.forEach(order => {
      let date;
      try {
        if (order.createdAt?._seconds) {
          // Format Firestore Timestamp
          date = new Date(order.createdAt._seconds * 1000);
        } else if (order.createdAt?.seconds) {
          // Format alternatif Firestore
          date = new Date(order.createdAt.seconds * 1000);
        } else if (order.createdAt) {
          // Format string ISO ou timestamp
          date = new Date(order.createdAt);
        } else {
          // Si pas de date, on utilise la date actuelle
          date = new Date();
        }

        const dateStr = date.toISOString().split('T')[0];
        
        if (!dailyOrders[dateStr]) {
          dailyOrders[dateStr] = 0;
          dailyRevenue[dateStr] = 0;
        }
        
        dailyOrders[dateStr]++;
        dailyRevenue[dateStr] += order.totalPrice || 0;
      } catch (error) {
        console.error('Erreur lors du traitement de la date:', order.createdAt, error);
      }
    });

    // Convertir en tableaux triés par date
    const sortedDates = Object.keys(dailyOrders).sort();
    const last30Days = sortedDates.slice(-30);
    
    const orderCounts = last30Days.map(date => dailyOrders[date]);
    const revenues = last30Days.map(date => dailyRevenue[date]);

    // Calculer les régressions
    const orderRegression = calculateLinearRegression(orderCounts);
    const revenueRegression = calculateLinearRegression(revenues);

    // Générer les prévisions pour les 7 prochains jours
    const futureDays = 7;
    const forecastDates = [];
    const orderForecasts = [];
    const revenueForecasts = [];

    for (let i = 0; i < futureDays; i++) {
      const forecastDate = new Date(last30Days[last30Days.length - 1]);
      forecastDate.setDate(forecastDate.getDate() + i + 1);
      forecastDates.push(forecastDate.toLocaleDateString('fr-DZ'));

      const orderForecast = Math.max(0, Math.round(
        orderRegression.slope * (orderCounts.length + i) + orderRegression.intercept
      ));
      orderForecasts.push(orderForecast);

      const revenueForecast = Math.max(0, Math.round(
        revenueRegression.slope * (revenues.length + i) + revenueRegression.intercept
      ));
      revenueForecasts.push(revenueForecast);
    }

    // Calculer les tendances
    const orderTrend = orderRegression.slope > 0 ? 'hausse' : 'baisse';
    const revenueTrend = revenueRegression.slope > 0 ? 'hausse' : 'baisse';
    const orderChange = Math.abs(Math.round(orderRegression.slope * 100));
    const revenueChange = Math.abs(Math.round(revenueRegression.slope * 100));

    return {
      dates: last30Days.map(date => new Date(date).toLocaleDateString('fr-DZ')),
      orderCounts,
      revenues,
      forecastDates,
      orderForecasts,
      revenueForecasts,
      trends: {
        orders: { direction: orderTrend, change: orderChange },
        revenue: { direction: revenueTrend, change: revenueChange }
      }
    };
  }, [orders]);

  const chartData = {
    labels: [...forecastData.dates, ...forecastData.forecastDates],
    datasets: [
      {
        label: 'Commandes (historique)',
        data: forecastData.orderCounts,
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Commandes (prévisions)',
        data: [...Array(forecastData.dates.length).fill(null), ...forecastData.orderForecasts],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderDash: [5, 5],
        fill: true,
        tension: 0.4
      }
    ]
  };

  const revenueChartData = {
    labels: [...forecastData.dates, ...forecastData.forecastDates],
    datasets: [
      {
        label: 'Chiffre d\'affaires (historique)',
        data: forecastData.revenues,
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Chiffre d\'affaires (prévisions)',
        data: [...Array(forecastData.dates.length).fill(null), ...forecastData.revenueForecasts],
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        borderDash: [5, 5],
        fill: true,
        tension: 0.4
      }
    ]
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            if (context.dataset.label.includes('Chiffre')) {
              return `${context.dataset.label}: ${context.parsed.y.toLocaleString('fr-DZ')} DA`;
            }
            return `${context.dataset.label}: ${context.parsed.y}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value, index, values) => {
            if (chartData.datasets[0].label.includes('Chiffre')) {
              return `${value.toLocaleString('fr-DZ')} DA`;
            }
            return value;
          }
        }
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-6">Prévisions des ventes</h3>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Tendance des commandes */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Tendance des commandes</h4>
            <p className="text-blue-900">
              Les commandes sont en <span className="font-medium">{forecastData.trends.orders.direction}</span> de{' '}
              <span className="font-medium">{forecastData.trends.orders.change}%</span> en moyenne par jour
            </p>
          </div>

          {/* Tendance du chiffre d'affaires */}
          <div className="bg-green-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-green-900 mb-2">Tendance du chiffre d'affaires</h4>
            <p className="text-green-900">
              Le chiffre d'affaires est en <span className="font-medium">{forecastData.trends.revenue.direction}</span> de{' '}
              <span className="font-medium">{forecastData.trends.revenue.change}%</span> en moyenne par jour
            </p>
          </div>
        </div>

        {/* Graphiques */}
        <div className="space-y-6">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-4">Prévision des commandes</h4>
            <Line data={chartData} options={options} />
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-4">Prévision du chiffre d'affaires</h4>
            <Line data={revenueChartData} options={options} />
          </div>
        </div>

        <div className="mt-6 text-sm text-gray-500">
          <p>* Les prévisions sont basées sur une analyse des tendances des 30 derniers jours</p>
          <p>* Les zones en pointillés représentent les prévisions pour les 7 prochains jours</p>
        </div>
      </div>
    </div>
  );
};

export default SalesForecast;
